import { useState, useCallback } from 'react';

import { api } from '@/lib/api';
import { fanficsCatalogApi, type FanficsCatalogType } from '@/lib/fanfics-catalog-api';
import { mapFandomListItem } from '@/hooks/map-fandom-list-item';

interface Fandom {
  id: number;
  title: string;
  sec_title: string;
  slug: string;
  is_universe: boolean;
  fanfic_cnt: number;
  group_crazy_title: string;
  group_id: string;
  permanentUrl?: string | null;
}

interface FandomSearchResult {
  fandoms: Fandom[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number | null;
  page: number;
  totalDocs: number;
  totalPages: number;
}

interface UseFandomsOptions {
  useNewApi?: boolean;
  type?: FanficsCatalogType;
}

/** Paginated result for infinite scroll dropdowns */
export interface PaginatedFandomResult {
  items: { id: string; name: string; count: number; isUniverse: boolean; permanentUrl?: string }[];
  hasNextPage: boolean;
}

const DROPDOWN_LIMIT = 50;

export function useFandoms(options: UseFandomsOptions = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { useNewApi = false, type = 'fanfic' } = options;

  const searchFandoms = useCallback(
    async (
      query: string,
      minFanfics: number = 0,
    ): Promise<
      { id: string; name: string; count: number; isUniverse: boolean; permanentUrl?: string }[]
    > => {
      setLoading(true);
      setError(null);

      try {
        const encodedQuery = encodeURIComponent(query);
        const requestPath = `/api/fandoms-custom?search=${encodedQuery}&limit=10&minFanfics=${minFanfics}${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<FandomSearchResult>(
          requestPath,
        );
        const results = response.data.fandoms.map(mapFandomListItem);

        return results;
      } catch (err) {
        console.error('Error searching fandoms:', err);
        setError(err instanceof Error ? err.message : 'Failed to search fandoms');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [type, useNewApi],
  );

  /** Paginated search for infinite scroll dropdowns */
  const searchFandomsPaginated = useCallback(
    async (
      query: string,
      page: number = 1,
      minFanfics: number = 0,
    ): Promise<PaginatedFandomResult> => {
      try {
        const encodedQuery = encodeURIComponent(query);
        const requestPath = `/api/fandoms-custom?search=${encodedQuery}&page=${page}&limit=${DROPDOWN_LIMIT}&minFanfics=${minFanfics}${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<FandomSearchResult>(
          requestPath,
        );
        const items = response.data.fandoms.map(mapFandomListItem);

        return { items, hasNextPage: response.data.hasNextPage };
      } catch (err) {
        console.error('Error searching fandoms paginated:', err);
        return { items: [], hasNextPage: false };
      }
    },
    [type, useNewApi],
  );

  const getFandoms = useCallback(
    async (
      page: number = 1,
      limit: number = 10,
      minFanfics: number = 1,
    ): Promise<FandomSearchResult> => {
      setLoading(true);
      setError(null);

      try {
        const requestPath = `/api/fandoms-custom?page=${page}&limit=${limit}&minFanfics=${minFanfics}${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<FandomSearchResult>(
          requestPath,
        );

        return response.data;
      } catch (err) {
        console.error('Error fetching fandoms:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch fandoms');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [type, useNewApi],
  );

  /** Get fandoms with paginated result for infinite scroll dropdowns */
  const getFandomsPaginated = useCallback(
    async (page: number = 1, minFanfics: number = 0): Promise<PaginatedFandomResult> => {
      try {
        const requestPath = `/api/fandoms-custom?page=${page}&limit=${DROPDOWN_LIMIT}&minFanfics=${minFanfics}${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<FandomSearchResult>(
          requestPath,
        );
        const items = response.data.fandoms.map(mapFandomListItem);

        return { items, hasNextPage: response.data.hasNextPage };
      } catch (err) {
        console.error('Error fetching fandoms paginated:', err);
        return { items: [], hasNextPage: false };
      }
    },
    [type, useNewApi],
  );

  return {
    searchFandoms,
    searchFandomsPaginated,
    getFandoms,
    getFandomsPaginated,
    loading,
    error,
  };
}
